<template>
  <div>
    <MapView
      ref="mapView"
      @updateBounds="onMapBoundsUpdated"
      @mapLoaded="$emit('mapLoaded')"
      @mapReady="$emit('mapReady')"
      autoDetectLocation
      :defaultZoom="defaultZoom"
      :loadingText="loadingText"
      :errorText="errorText"
      :mapViewHeight="mapViewHeight"
      :streetViewControl="streetViewControl"
      :mapTypeControl="mapTypeControl">

      <template slot-scope="{ google, map, mapState }">
        <TaskMapRoute
          v-for="route in taskRoutes"
          :key="`route-${route.id}`"
          :routeColour="route.highlightColour"
          :polylineCode="route.routePolylineCode"
          :itemPoints="route.itemPoints"
          :google="google"
          :map="map"
          @click="onTaskRouteClicked(route, $event)"
          />
        <MapMarker
          v-for="marker in markerList"
          :key="`marker-${marker.id}`"
          :position="marker.position"
          :type="marker.type"
          :label="marker.label"
          :fillColour="marker.fillColour"
          :strokeColour="marker.strokeColour"
          :labelColour="marker.labelColour"
          :google="google"
          :map="map"
          @click="onMarkerClicked(marker, $event)"
        />
        <MapTrackable
          v-for="user of userTrackingList"
          :key="`user-${user.id}`"
          :map="map"
          :google="google"
          :colour="user.highlightColour"
          :position="user.position"
          :rotation="user.heading"
          :name="user.name"
          @click="onUserClicked(user, $event)"
        />
        
        <slot
          :google="google"
          :map="map"
          :mapState="mapState"
        />
      </template>
    </MapView>

    <MapTaskPopover ref="taskPopover" />
    <MapOrdersPopover ref="ordersPopover" :dragEnabled="draggableMarkersEnabled" />
    <MapUserPopover ref="userPopover" />
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import ColourMixin from '@/mixins/colour'
import GoogleMapMixin from '@/mixins/googleMap'

import MathsUtils from '@/utils/mathsUtils'
import MapUtils from '@/utils/mapUtils'

import MapView from '@/components/maps/mapView'
import MapMarker from '@/components/maps/mapMarker'
import MapPolyline from '@/components/maps/mapPolyline'
import MapTrackable from '@/components/maps/mapTrackable'
import TaskMapRoute from '@/modules/tasks/components/taskMapRoute'
import MapTaskPopover from '@/modules/tasks/components/mapTaskPopover'
import MapOrdersPopover from '@/modules/tasks/components/mapOrdersPopover'
import MapUserPopover from '@/modules/tasks/components/mapUserPopover'

export default {
  name: 'TaskManagerMapView',
  mixins: [ColourMixin, GoogleMapMixin],
  components: {
    MapView,
    MapMarker,
    MapPolyline,
    MapTrackable,
    TaskMapRoute,
    MapTaskPopover,
    MapOrdersPopover,
    MapUserPopover
  },
  props: {
    currentDay: Date,
    loadingText: String,
    errorText: String,

    mapViewHeight: {
      type: String,
      default: '65vh'
    },
    streetViewControl: {
      type: Boolean,
      default: false
    },
    mapTypeControl: {
      type: Boolean,
      default: false
    },
    defaultZoom: {
      type: Number,
      default: 16
    },

    draggableMarkersEnabled: {
      type: Boolean,
      default: false
    },
    
    adjustBoundsOnLoad: {
      type: Boolean,
      default: true
    },
    displayUserLocations: {
      type: Boolean,
      default: true
    },
    displayRoutes: {
      type: Boolean,
      default: true
    },
    displayOrders: {
      type: Boolean,
      default: true
    },

    mapUserFilter: Array,
    mapOrderFilter: Array
  },
  data() {
    return {
      firstMapDataLoad: true,
      markerList: [],
      taskRoutes: [],
      userTrackingList: [],
      mapBounds: null
    }
  },
  watch: {
    currentDay: function() {
      this.debouncedMapUpdate(this.mapBounds)
    },
    mapOrderFilter: {
      deep: true,
      handler: function() {
        this.debouncedMapUpdate(this.mapBounds)
      }
    },
    mapUserFilter: {
      deep: true,
      handler: function() {
        this.debouncedMapUpdate(this.mapBounds)
      }
    }
  },
  created() {
    this.debouncedMapUpdate = _.debounce(this.requestVisibleMapData, 500)
  },
  mounted() {
    // Join the live updates room & listen to location updates
    this.subscribeRoom(this.$socketService.SocketMgrRooms.locationLiveUpdates)
    this.addSocketEventListener(this.$socketService.SocketMgrMessages.locationUpdated, (args) => {
      let user = this.userTrackingList.find(user => user.id == args.userId)
      if(user) {
        this.updateUserPosition(user, args.heading, parseFloat(args.lng || 0), parseFloat(args.lat || 0))

      } else {
        // If a user filter is setup & it isn't in the list, ignore it
        if(this.mapUserFilter) {
          if(!this.mapUserFilter.includes(args.userId))
            return
        }

        // Add it to the list
        this.setupTrackingUser({
          id: args.userId,
          name: args.name,
          highlightColour: args.highlightColour,
          position: { lng: parseFloat(args.lng || 0), lat: parseFloat(args.lat || 0) },
          heading: args.heading,
          locationLastUpdate: moment().toISOString()
        })

        this.$emit('userLocationUpdated')
      }
    })

    this.subscribeEndpoint('/tasks/map', () => {
      this.debouncedMapUpdate(this.mapBounds)
    })
  },
  beforeDestroy() {
    this.unsubscribeRoom(this.$socketService.SocketMgrRooms.locationLiveUpdates)
    this.removeSocketEventListener(this.$socketService.SocketMgrMessages.locationUpdated)

    this.unsubscribeEndpoint('/tasks/map')
  },
  methods: {
    onMapBoundsUpdated(bounds) {
      this.mapBounds = bounds
      this.debouncedMapUpdate(bounds)
    },

    refreshMapData() {
      this.debouncedMapUpdate(this.mapBounds)
    },

    requestVisibleMapData(bounds) {
      if(!bounds)
        return
        
      var routeDate = this.currentDay || moment().toDate()
      let includeUserLocations = this.$props.displayUserLocations
      let includeRoutes = this.$props.displayRoutes
      let includeOrders = this.$props.displayOrders

      var totalPointList = []

      // Ignore if zero bounds
      // Happens when map loaded but not yet visible
      if(bounds.min.lat == bounds.max.lat && bounds.min.lng == bounds.max.lng)
        return

      var requestData = {
        minLng: bounds.min.lng,
        minLat: bounds.min.lat,
        maxLng: bounds.max.lng,
        maxLat: bounds.max.lat,
        routeDate: routeDate.toISOString(),
        includeRoutes: includeRoutes,
        includeUserLocations: includeUserLocations,
        includeOrders: includeOrders,
        filter: {}
      }

      // Filter orders
      if(this.mapOrderFilter) {
        let fieldList = this.mapOrderFilter.map(filter => filter.field)
        fieldList.forEach(field => {
          var filterList = this.mapOrderFilter.filter(selected => selected.field == field).map(selected => selected.value).join(',')

          if(filterList.length > 0)
            requestData.filter[field] = filterList
        })
      }

      this.$apiService.tasksService.calculateMappingData(requestData)
      .then(response => {
        this.markerList = response.data.markers.map(marker => {

          var markerInfo = {
            id: this.getUniqueListKey(),
            position: marker.position,
            type: MapMarker.MapMarkerTypes.simple,
            label: marker.orders.length > 1 ? (marker.orders.length+'') : '',
            orders: marker.orders,
            fillColour: '#808080'
          }

          if(marker.allAssigned && marker.tasks && marker.tasks.length > 0) {
            // If multiple tasks assigned to this marker, show a 'mixed' colour
            let markerColour = marker.tasks[0].highlightColour
            if(marker.tasks.length > 1) {
              markerColour = '#487eb0'
            }

            markerInfo.type = MapMarker.MapMarkerTypes.squarePin
            markerInfo.labelColour = this.textColourForBackground(markerColour)
            markerInfo.fillColour = markerColour
            markerInfo.label =(marker.orders.length+'')
          }
          else if(!marker.allAssigned && marker.tasks && marker.tasks.length > 0) {
            let markerColour = '#487eb0'

            markerInfo.type = MapMarker.MapMarkerTypes.customPin
            markerInfo.labelColour = this.textColourForBackground(markerColour)
            markerInfo.fillColour = markerColour
            markerInfo.label = (marker.orders.length+'')            
          }

          return markerInfo
        })

        totalPointList = _.concat(totalPointList, this.markerList.map(marker => marker.position))

        this.taskRoutes = (response.data.taskRoutes || []).map(taskRoute => {
          var output = _.cloneDeep(taskRoute)
          output.itemPoints = []
          if(taskRoute.routeStartCoordinate) {
            output.itemPoints.push(taskRoute.routeStartCoordinate)
          }

          if(taskRoute.taskItems) {
            taskRoute.taskItems.forEach(taskItem => {
              if(taskItem.locationCoordinate) {
                output.itemPoints.push(taskItem.locationCoordinate)
              }
            })
          }

          // Set route to match colour of the first user assigned to the task
          if(taskRoute.users && taskRoute.users.length > 0) {
            output.highlightColour = taskRoute.users[0].highlightColour
          }

          return output
        })

        response.data.users.forEach(user => {
          // If a user filter is setup & it isn't in the list, ignore it
          if(this.mapUserFilter) {
            if(!this.mapUserFilter.includes(user.id))
              return
          }

          let currentUser = this.userTrackingList.find(listUser => listUser.id == user.id)
          if(!currentUser) {
            this.setupTrackingUser({
              id: user.id,
              name: user.name,
              highlightColour: user.highlightColour,
              position: user.locationCoordinate,
              heading: user.locationHeading,
              locationLastUpdate: moment(user.locationLastUpdate).toISOString()
            })
            
          } else {
            // Update current user location data only if the location update isn't older
            if(moment(currentUser.locationLastUpdate).isBefore(moment(user.locationLastUpdate)) && user.locationCoordinate) {

              this.$set(currentUser, 'position', { lng: parseFloat(user.locationCoordinate.lng || 0), lat: parseFloat(user.locationCoordinate.lat || 0) })
              currentUser.heading = user.heading
              currentUser.locationLastUpdate = moment(user.locationLastUpdate).toISOString()
            }
          }
        })

        // Add to total point list
        this.userTrackingList.forEach(user => {
          totalPointList.push(user.position)
        })
        this.taskRoutes.forEach(route => {
          totalPointList = _.concat(totalPointList, route.itemPoints)
        })

        // If the first data load, fit to the bounds of the markers
        if(this.firstMapDataLoad) {
          if(this.$props.adjustBoundsOnLoad)
            this.$refs.mapView.panToFitPointList(totalPointList)
          this.firstMapDataLoad = false
        }
      })
      .catch(err => {
        console.error(err)
      })
    },

    panToFitPointList(pointList) {
      if(this.$refs.mapView)
        this.$refs.mapView.panToFitPointList(pointList)
    },

    onMarkerClicked(marker, evt) {
      if(marker.orders && marker.orders.length > 0) {

        var markerElement = this.getElementFromClickEvent(evt)
        
        if(markerElement)
          this.$refs.ordersPopover.displayFromElement(markerElement, marker.orders)
      }
    },
    onTaskRouteClicked(route, evt) {
      //console.dir(evt)
      //this.$refs.taskPopover.displayFromElement(evt.vb.target, route)
    },
    onUserClicked(user, evt) {
      let mapElement = this.getElementFromClickEvent(evt)
      if(mapElement) {
        this.$refs.userPopover.displayFromElement(mapElement, user)
      }
    },

    setupTrackingUser(userInfo) {
      this.userTrackingList.push({
        id: userInfo.id,
        name: userInfo.name,
        highlightColour: userInfo.highlightColour,
        position: userInfo.position,
        heading: userInfo.heading,
        locationLastUpdate: userInfo.locationLastUpdate
      })
    },

    updateUserPosition(user, heading, longitude, latitude) {
      this.$set(user, 'position', { lng: longitude, lat: latitude })
      user.heading = heading

      this.$emit('userLocationUpdated')
    },
  }
  
}
</script>